import {
  createDocument,
  getDocumentUpdateJobStatus,
} from '@modusign/openapi-client-external-document';
import {
  all,
  call,
  delay,
  put,
  race,
  retry,
  select,
  take,
  takeEvery,
  takeLeading,
} from 'redux-saga/effects';
import { PromiseType } from 'utility-types';

import LoadingStatus from '@libs/loading-status';

import workspaceService from '@app/libs/workspaceService';

import uploadFile from './libs/uploadFile';
import {
  batchUpdateStarted,
  DocumentFile,
  DocumentFileWithFileKey,
  documentUploadFulfilled,
  documentUploadRejected,
  documentUploadStart,
  fileUploadFulfilled,
  fileUploadRejected,
  fileUploadStart,
  getDocumentByIndex,
  getExternalDocumentUploadStatus,
  getFileByIndex,
  getNextDocumentToUpload,
  getNextFileToUpload,
  initializeExternalDocumentBatchJob,
  retryFailedDocuments,
  startUpload,
  updateBatchJobStatus,
} from './state';

const UPLOAD_CONCURRENTY = 10;

function allFileHasFileKey(files: DocumentFile[]): files is DocumentFileWithFileKey[] {
  return files.every((file) => file.fileKey);
}

export function* uploadDocumentStart(action: ReturnType<typeof documentUploadStart>) {
  const { docIdx } = action.payload;

  const document: ReturnType<typeof getDocumentByIndex> = yield select((state) =>
    getDocumentByIndex(state, docIdx)
  );

  try {
    const file = document.files.find((file) => file.isContract);
    const attachments = document.files.filter((file) => !file.isContract);

    if (!file) {
      throw new Error(`Document "${document.name}" does not have a contract file`);
    }

    if (!file.fileKey) {
      throw new Error(
        `File "${file.name}" of document "${document.name}" does not have a file key`
      );
    }

    if (!allFileHasFileKey(attachments)) {
      throw new Error(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        `Attachment "${attachments.find((f) => !f.fileKey)!.name}" of document "${
          document.name
        }" does not have a file key`
      );
    }

    yield retry(2, 100, createDocument, workspaceService.getWorkspaceId(), {
      file: {
        name: file.name,
        key: file.fileKey,
      },
      attachments: attachments.map((file) => ({ name: file.name, key: file.fileKey })),
      title: document.name,
      dateOfCompletion: document.metadata?.dateOfCompletion,
      documentPeriod: document.metadata?.documentPeriod,
      memo: document.metadata?.memo,
      originalLocation: document.metadata?.originalLocation,
      signers: document.metadata?.signers?.map((signer) => ({
        ...signer,
        personInCharge: signer.personInCharge || undefined,
        externalSigner: signer.externalSigner || false,
      })),
      personInCharge: document.metadata?.personInCharge || undefined,
      renewal: document.metadata?.renewal,
      status: document.metadata?.status,
      type: document.metadata?.type,
      labelIds: document.metadata?.labels.map(({ id }) => id) || [],
    });
    yield put(documentUploadFulfilled({ docIdx }));
  } catch (e) {
    yield put(documentUploadRejected({ docIdx, error: e }));
  }
}

export function* uploadFileStart(action: ReturnType<typeof fileUploadStart>) {
  const { docIdx, fileIdx } = action.payload;
  const file: ReturnType<typeof getFileByIndex> = yield select((state) =>
    getFileByIndex(state, docIdx, fileIdx)
  );

  try {
    // 해당 파일에 접근할 수 있는지 확인
    yield call(() => file.file.arrayBuffer());
    const fileKey: PromiseType<ReturnType<typeof uploadFile>> = yield retry(
      2,
      100,
      uploadFile,
      file.file,
      file.isContract ? 'DOCUMENT' : 'ATTACHMENT'
    );

    yield put(fileUploadFulfilled({ docIdx, fileIdx, fileKey }));
  } catch (e) {
    yield put(fileUploadRejected({ docIdx, fileIdx, error: e }));
  }
}

// TODO: write test
// 외부 문서 업로드 동시성 관리 사가
// 각 문서의 파일을 업로드한 후 문서를 업로드합니다.
// 동시에 최대 5개까지 업로드 합니다.
// 참고: https://github.com/modusign/modusign-client/pull/6269
export function* uploadExternalDocuments() {
  let concurrentUploadCount = 0;

  const externalDocumentUploadStatus: ReturnType<typeof getExternalDocumentUploadStatus> =
    yield select(getExternalDocumentUploadStatus);
  while (externalDocumentUploadStatus === LoadingStatus.PENDING) {
    if (concurrentUploadCount >= UPLOAD_CONCURRENTY) {
      yield take([
        documentUploadFulfilled.type,
        documentUploadRejected.type,
        fileUploadFulfilled.type,
        fileUploadRejected.type,
      ]);
      concurrentUploadCount -= 1;
    }

    const documentToUpload: ReturnType<typeof getNextDocumentToUpload> =
      yield select(getNextDocumentToUpload);
    if (documentToUpload) {
      yield put(documentUploadStart({ docIdx: documentToUpload.idx }));
      concurrentUploadCount += 1;
      // eslint-disable-next-line no-continue
      continue;
    }

    const fileToUpload: ReturnType<typeof getNextFileToUpload> = yield select(getNextFileToUpload);
    if (fileToUpload) {
      yield put(fileUploadStart({ docIdx: fileToUpload.docIdx, fileIdx: fileToUpload.fileIdx }));
      concurrentUploadCount += 1;
      // eslint-disable-next-line no-continue
      continue;
    }
    yield delay(1000);
  }
}

export function* startPollBatchUpdates(action: ReturnType<typeof batchUpdateStarted>) {
  const { jobId } = action.payload;

  const startTime = Date.now();

  // 최대 10분 동안 polling 합니다.
  // 참고: https://github.com/modusign/modusign-client/pull/6831#discussion_r902250802
  while (Date.now() - startTime < 10 * 60 * 1000) {
    const job: PromiseType<ReturnType<typeof getDocumentUpdateJobStatus>> = yield retry(
      2,
      1000,
      getDocumentUpdateJobStatus,
      workspaceService.getWorkspaceId(),
      jobId
    );

    yield put(updateBatchJobStatus({ jobId, count: job.count }));
    // 일괄 업데이트를 시작할 때 각 계약 업데이트 task를 비동기적으로 생성해 업데이트 시작 초기에 pending, failure, success가 모두 0이 올 수 있습니다.
    // 이 경우 polling을 멈추지 않고 계속합니다.
    if (job.count.pending === 0 && job.count.failure + job.count.success !== 0) {
      return;
    }
    const [initialized]: [ReturnType<typeof initializeExternalDocumentBatchJob>, boolean] =
      yield race([take(initializeExternalDocumentBatchJob.type), delay(1000)]);

    if (initialized) {
      return;
    }
  }
}

export default function* externalDocumentSaga() {
  yield all([
    takeLeading([startUpload.type, retryFailedDocuments.type], uploadExternalDocuments),
    takeEvery(fileUploadStart.type, uploadFileStart),
    takeEvery(documentUploadStart.type, uploadDocumentStart),
    takeEvery(batchUpdateStarted.type, startPollBatchUpdates),
  ]);
}
